import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";

import { AuthSessionManager } from "../../auth/AuthSessionManager";

const AuthContext = createContext();

const FORCED_LOGOUT_DELAY_MS = 3000;

function getSessionExpiredCopy(isImpersonated) {
  if (isImpersonated) {
    return {
      title: "Sessione impersonificata scaduta",
      message: "Verrai reindirizzato al login.",
    };
  }
  return {
    title: "Sessione scaduta",
    message: "Effettua nuovamente l'accesso per continuare.",
  };
}

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const isImpersonateBootstrap = location.pathname === "/impersonate-login";

  const [{ token, principal, role }, setState] = useState(() => {
    const boot = AuthSessionManager.bootstrap();
    return {
      token: boot.token,
      principal: boot.principal,
      role: boot.role ?? boot.principal?.role ?? null,
    };
  });

  const clearExpirationTimers = useCallback(() => {
    if (expirationTimerRef.current) {
      clearTimeout(expirationTimerRef.current);
      expirationTimerRef.current = null;
    }
    if (forcedLogoutTimerRef.current) {
      clearTimeout(forcedLogoutTimerRef.current);
      forcedLogoutTimerRef.current = null;
    }
  }, []);

  const loginWithToken = (tokenValue) => {
    clearExpirationTimers();
    expirationHandledRef.current = false;
    setSessionExpiredOpen(false);

    const principalValue = AuthSessionManager.loginWithToken(tokenValue);
    if (!principalValue) return;

    const storedToken = AuthSessionManager.getToken();
    setState({
      token: storedToken ?? tokenValue,
      principal: principalValue,
      role: principalValue.role,
    });

    return principalValue;
  };

  const logout = useCallback(() => {
    AuthSessionManager.logout();
    setState({ token: null, principal: null, role: null });
  }, []);

  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredCopy, setSessionExpiredCopy] = useState(() => getSessionExpiredCopy(false));
  const expirationTimerRef = useRef(null);
  const forcedLogoutTimerRef = useRef(null);
  const expirationHandledRef = useRef(false);

  const triggerExpirationFlow = useCallback(
    (isImpersonated) => {
      if (expirationHandledRef.current) return;
      expirationHandledRef.current = true;
      setSessionExpiredCopy(getSessionExpiredCopy(isImpersonated));
      setSessionExpiredOpen(true);
      forcedLogoutTimerRef.current = setTimeout(() => {
        setSessionExpiredOpen(false);
        logout();
      }, FORCED_LOGOUT_DELAY_MS);
    },
    [logout]
  );

  useEffect(() => {
    const handleChange = () => {
      expirationHandledRef.current = false;
      const boot = AuthSessionManager.bootstrap();
      setState({
        token: boot.token,
        principal: boot.principal,
        role: boot.role ?? boot.principal?.role ?? null,
      });
    };

    window.addEventListener("wcp:auth:change", handleChange);
    window.addEventListener("wcp:auth:logout", handleChange);
    return () => {
      window.removeEventListener("wcp:auth:change", handleChange);
      window.removeEventListener("wcp:auth:logout", handleChange);
    };
  }, []);

  useEffect(() => {
    const handleSessionInvalid = () => {
      if (isImpersonateBootstrap) {
        return;
      }
      triggerExpirationFlow(principal?.isImpersonated === true);
    };

    window.addEventListener("wcp:auth:session-invalid", handleSessionInvalid);
    return () => window.removeEventListener("wcp:auth:session-invalid", handleSessionInvalid);
  }, [isImpersonateBootstrap, principal?.isImpersonated, triggerExpirationFlow]);

  useEffect(() => {
    if (isImpersonateBootstrap) {
      clearExpirationTimers();
      expirationHandledRef.current = false;
      setSessionExpiredOpen(false);
      return undefined;
    }

    clearExpirationTimers();
    if (!token) {
      expirationHandledRef.current = false;
      setSessionExpiredOpen(false);
      return undefined;
    }

    const expMs = typeof principal?.exp === "number" ? principal.exp : null;
    if (!expMs) {
      return undefined;
    }

    const msUntilDialog = expMs - Date.now() - 1000;
    if (msUntilDialog <= 0) {
      triggerExpirationFlow(principal?.isImpersonated === true);
      return undefined;
    }

    expirationTimerRef.current = setTimeout(
      () => triggerExpirationFlow(principal?.isImpersonated === true),
      msUntilDialog
    );

    return clearExpirationTimers;
  }, [
    clearExpirationTimers,
    isImpersonateBootstrap,
    principal,
    token,
    triggerExpirationFlow,
  ]);

  return (
    <AuthContext.Provider
      value={{
        token,
        role,
        principal,
        vendorId: principal?.vendorId ?? null,
        loginId: principal?.userId ?? null,
        userId: principal?.userId ?? null,
        isImpersonated: principal?.isImpersonated === true,
        profile: principal?.profile ?? null,
        loginWithToken,
        logout,
      }}
    >
      {children}
      <Dialog
        open={sessionExpiredOpen}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogContent sx={{ py: 3, textAlign: "center" }}>
          <Typography sx={{ fontFamily: "Rubik", fontWeight: 700, color: "#0b3d87", fontSize: "1.1rem" }}>
            {sessionExpiredCopy.title}
          </Typography>
          <Typography sx={{ mt: 1, fontFamily: "Rubik", color: "#4f5d73", fontSize: "0.92rem" }}>
            {sessionExpiredCopy.message}
          </Typography>
        </DialogContent>
      </Dialog>
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
